import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faHandsHelping,
  faCalendarDays,
  faClock,
  faUser,
  faSearch,
  faEye,
} from "@fortawesome/free-solid-svg-icons";

import { useHerKart } from "./hooks/useHerKart";

const STATUS_STYLES = {
  Requested: "border-amber-500/30 bg-amber-500/10 text-amber-300",
  Confirmed: "border-cyan-500/30 bg-cyan-500/10 text-cyan-300",
  Completed: "border-emerald-500/30 bg-emerald-500/10 text-emerald-300",
  Cancelled: "border-red-500/30 bg-red-500/10 text-red-300",
};

// Dual-mode: shows the customer's own bookings on /herkart/service-bookings/mine,
// and the provider's incoming bookings on /herkart/service-bookings/manage.
// Mirrors the split already used by Orders (HerKartOrders.jsx = seller-only) but,
// unlike Orders, this one properly scopes both views server-side.
const HerKartServiceBookings = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { fetchMyServiceBookings, fetchProviderServiceBookings } = useHerKart();

  const isProviderView = location.pathname.includes("manage");

  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");

  const load = async () => {
    setLoading(true);
    try {
      const response = isProviderView
        ? await fetchProviderServiceBookings()
        : await fetchMyServiceBookings();

      if (response?.success) {
        setBookings(response.bookings || []);
      }
    } catch (e) {
      console.error("Bookings fetch error:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isProviderView]);

  const filtered = useMemo(() => {
    let list = [...bookings];

    if (statusFilter !== "All") {
      list = list.filter((b) => b.status === statusFilter);
    }

    if (search.trim()) {
      const keyword = search.toLowerCase();
      list = list.filter((b) =>
        [b.bookingNo, b.serviceName, b.customer?.name, b.customer?.mobile]
          .join(" ")
          .toLowerCase()
          .includes(keyword)
      );
    }

    return list;
  }, [bookings, search, statusFilter]);

  return (
    <div className="min-h-screen bg-[#050816] text-white">
      <div className="sticky top-0 z-40 border-b border-white/10 bg-[#050816]/95 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center gap-3 px-4 py-4">
          <Link
            to="/herkart/dashboard"
            className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/10 hover:bg-white/20"
          >
            <FontAwesomeIcon icon={faArrowLeft} />
          </Link>
          <div>
            <h1 className="text-xl font-bold">
              {isProviderView ? "Service Booking Management" : "My Service Bookings"}
            </h1>
            <p className="text-xs text-slate-400">
              {isProviderView
                ? "Bookings customers have placed with you"
                : "Your service booking history"}
            </p>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-6xl px-4 py-6">
        <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
          <div className="relative">
            <FontAwesomeIcon icon={faSearch} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by booking no, service, customer..."
              className="w-full rounded-xl border border-white/10 bg-[#0b1120] py-3 pl-11 pr-4 text-sm text-white placeholder:text-slate-500 outline-none focus:border-pink-500"
            />
          </div>

          <div className="mt-4 flex items-center gap-2 overflow-x-auto pb-1">
            {["All", "Requested", "Confirmed", "Completed", "Cancelled"].map((s) => (
              <button
                key={s}
                onClick={() => setStatusFilter(s)}
                className={`whitespace-nowrap rounded-full px-3 py-1.5 text-xs transition ${
                  statusFilter === s
                    ? "bg-pink-600 text-white"
                    : "border border-white/10 bg-white/5 text-slate-300 hover:bg-white/10"
                }`}
              >
                {s}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="mt-16 text-center text-slate-400">Loading bookings...</div>
        ) : filtered.length === 0 ? (
          <div className="mt-16 rounded-3xl border border-dashed border-white/10 bg-white/5 p-10 text-center">
            <FontAwesomeIcon icon={faHandsHelping} className="mx-auto mb-4 text-4xl text-slate-500" />
            <h3 className="text-xl font-semibold">No bookings found</h3>
            <p className="mt-2 text-sm text-slate-400">
              {bookings.length === 0
                ? isProviderView
                  ? "No one has booked your services yet."
                  : "You haven't booked any services yet."
                : "Try a different search or status filter."}
            </p>
          </div>
        ) : (
          <div className="mt-6 space-y-4">
            {filtered.map((booking) => (
              <div
                key={booking._id}
                className="flex flex-col gap-3 rounded-2xl border border-white/10 bg-white/5 p-5 md:flex-row md:items-center md:justify-between"
              >
                <div>
                  <div className="flex items-center gap-3">
                    <h3 className="font-semibold text-white">{booking.serviceName}</h3>
                    <span
                      className={`rounded-full border px-2 py-0.5 text-xs font-semibold ${
                        STATUS_STYLES[booking.status] || STATUS_STYLES.Requested
                      }`}
                    >
                      {booking.status}
                    </span>
                  </div>

                  <p className="mt-1 text-xs text-slate-400">Booking No: {booking.bookingNo}</p>

                  <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-400">
                    {isProviderView && (
                      <span className="flex items-center gap-1">
                        <FontAwesomeIcon icon={faUser} className="text-pink-400" />
                        {booking.customer?.name} ({booking.customer?.mobile})
                      </span>
                    )}
                    <span className="flex items-center gap-1">
                      <FontAwesomeIcon icon={faCalendarDays} className="text-pink-400" />
                      {new Date(booking.preferredDate).toLocaleDateString()}
                    </span>
                    <span className="flex items-center gap-1">
                      <FontAwesomeIcon icon={faClock} className="text-pink-400" />
                      {booking.preferredSlot}
                    </span>
                  </div>
                </div>

                <div className="flex items-center justify-between gap-4 md:flex-col md:items-end">
                  <div className="text-lg font-bold text-pink-400">₹{booking.price}</div>
                  <button
                    onClick={() => navigate(`/herkart/service-bookings/${booking.bookingNo}`)}
                    className="flex items-center gap-2 rounded-xl border border-fuchsia-500/20 px-3 py-1.5 text-sm font-medium text-fuchsia-300 hover:bg-fuchsia-500/10"
                  >
                    <FontAwesomeIcon icon={faEye} />
                    {isProviderView ? "Manage" : "View"}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default HerKartServiceBookings;
